import { useState, useMemo } from "react";
import { X, Plus, Minus, ShoppingBag, Bike, Loader2, CheckCircle2, Clock } from "lucide-react";
import { toast } from "sonner";
import { api, apiErrorMessage } from "@/lib/api";

const money = (n) => `$${Number(n).toFixed(2).replace(/\.00$/, "")}`;

function timeSlots() {
  const out = ["As soon as possible"];
  const now = new Date();
  let t = new Date(now.getTime() + 20 * 60000);
  const m = t.getMinutes();
  t.setMinutes(m + ((15 - (m % 15)) % 15), 0, 0);
  for (let i = 0; i < 20; i++) {
    out.push(t.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }));
    t = new Date(t.getTime() + 15 * 60000);
  }
  return out;
}

export default function TakeawayOrderModal({ open, onClose, takeaway }) {
  const [qty, setQty] = useState({});
  const [fulfilment, setFulfilment] = useState("pickup");
  const [requestedTime, setRequestedTime] = useState("As soon as possible");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [address, setAddress] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [placed, setPlaced] = useState(null); // { order_number }

  const items = takeaway?.items || [];
  const onlinePayment = !!takeaway?.online_payment;
  const deliveryFee = fulfilment === "delivery" ? Number(takeaway?.delivery_fee || 0) : 0;
  const slots = useMemo(timeSlots, [open]);

  const subtotal = useMemo(
    () => items.reduce((s, it) => s + (qty[it.id] || 0) * it.price, 0),
    [items, qty]
  );
  const total = subtotal + deliveryFee;
  const count = Object.values(qty).reduce((a, b) => a + b, 0);

  if (!open) return null;

  const close = () => { setPlaced(null); onClose(); };

  const bump = (id, d) =>
    setQty((q) => {
      const n = Math.max(0, (q[id] || 0) + d);
      const next = { ...q };
      if (n === 0) delete next[id];
      else next[id] = n;
      return next;
    });

  const validate = () => {
    if (count === 0) { toast.error("Add at least one item."); return false; }
    if (!name.trim() || !phone.trim()) { toast.error("Please add your name and phone number."); return false; }
    if (fulfilment === "delivery" && !address.trim()) { toast.error("Please add a delivery address."); return false; }
    return true;
  };

  const submit = async () => {
    if (!validate()) return;
    setBusy(true);
    const cart = Object.entries(qty).map(([id, q]) => ({ id, qty: q }));
    const base = { items: cart, fulfilment, requested_time: requestedTime, name, phone, email, address, notes };
    try {
      if (onlinePayment) {
        const { data } = await api.post("/takeaway/checkout", { ...base, origin_url: window.location.origin });
        window.location.href = data.checkout_url;
      } else {
        const { data } = await api.post("/takeaway/order", base);
        setPlaced({ order_number: data.order_number });
      }
    } catch (err) {
      toast.error(apiErrorMessage(err));
      setBusy(false);
    }
  };

  const Toggle = ({ id, icon: Icon, label }) => (
    <button
      type="button"
      onClick={() => setFulfilment(id)}
      data-testid={`fulfilment-${id}`}
      className={`flex-1 flex items-center justify-center gap-2 font-head font-bold py-3 rounded-xl border-2 border-foreground transition-colors ${
        fulfilment === id ? "bg-foreground text-background" : "hover:bg-muted"
      }`}
    >
      <Icon size={16} /> {label}
    </button>
  );

  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-foreground/60 backdrop-blur-sm p-0 sm:p-4"
      onClick={close} data-testid="takeaway-modal">
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-background w-full sm:max-w-lg sm:rounded-3xl rounded-t-3xl border-2 border-foreground max-h-[92vh] overflow-y-auto"
      >
        <div className="sticky top-0 bg-background border-b-2 border-foreground/10 px-6 py-4 flex items-center justify-between">
          <h3 className="font-head font-extrabold text-xl">{placed ? "Order placed" : "Order Takeaway"}</h3>
          <button onClick={close} data-testid="takeaway-close" className="p-1.5 rounded-full hover:bg-muted"><X size={20} /></button>
        </div>

        {placed ? (
          <div className="p-8 text-center" data-testid="takeaway-confirmation">
            <CheckCircle2 size={52} className="mx-auto text-secondary" />
            <p className="font-mono-plex text-foreground/60 mt-4">Your order number</p>
            <p className="font-head font-black text-5xl mt-1" data-testid="confirmation-order-number">#{placed.order_number}</p>
            <p className="text-foreground/70 mt-4">Thanks {name.split(" ")[0] || ""}! We'll have it ready shortly.
              {" "}Pay when you {fulfilment === "delivery" ? "receive your order" : "collect"}.</p>
            <div className="flex items-center justify-center gap-2 mt-3 text-foreground/60 font-mono-plex text-sm">
              <Clock size={15} /> {fulfilment === "delivery" ? "Delivery" : "Pickup"}: {requestedTime}
            </div>
            <button onClick={close} data-testid="confirmation-done" className="mt-6 font-head font-bold border-2 border-foreground rounded-full px-6 py-2.5 hover:bg-muted">Done</button>
          </div>
        ) : (
          <div className="p-6 space-y-5">
            <div className="space-y-2">
              {items.map((it) => (
                <div key={it.id} data-testid={`cart-item-${it.id}`} className="flex items-center gap-3 border-2 border-foreground/15 rounded-xl p-3">
                  <div className="flex-1 min-w-0">
                    <p className="font-head font-bold leading-tight">{it.name} <span className="text-foreground/50 font-mono-plex text-sm">{money(it.price)}</span></p>
                    {it.desc && <p className="text-sm text-foreground/55 leading-snug">{it.desc}</p>}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button onClick={() => bump(it.id, -1)} data-testid={`cart-minus-${it.id}`} className="w-8 h-8 rounded-full border-2 border-foreground flex items-center justify-center hover:bg-muted disabled:opacity-30" disabled={!qty[it.id]}><Minus size={14} /></button>
                    <span className="w-6 text-center font-mono-plex" data-testid={`cart-qty-${it.id}`}>{qty[it.id] || 0}</span>
                    <button onClick={() => bump(it.id, 1)} data-testid={`cart-plus-${it.id}`} className="w-8 h-8 rounded-full border-2 border-foreground flex items-center justify-center hover:bg-muted"><Plus size={14} /></button>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex gap-2">
              <Toggle id="pickup" icon={ShoppingBag} label="Pick up" />
              {takeaway?.delivery_enabled && <Toggle id="delivery" icon={Bike} label={`Delivery${deliveryFee ? ` (+${money(takeaway.delivery_fee)})` : ""}`} />}
            </div>

            <label className="block">
              <span className="font-head font-bold text-sm flex items-center gap-1.5"><Clock size={14} /> {fulfilment === "delivery" ? "Requested delivery time" : "Requested pickup time"}</span>
              <select className="w-full mt-2 border-2 border-foreground rounded-xl px-3 py-2.5 bg-background" value={requestedTime} onChange={(e) => setRequestedTime(e.target.value)} data-testid="takeaway-time">
                {slots.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </label>

            <div className="grid grid-cols-2 gap-3">
              <input className="col-span-1 border-2 border-foreground rounded-xl px-3 py-2.5 bg-background" placeholder="Your name" value={name} onChange={(e) => setName(e.target.value)} data-testid="takeaway-name" />
              <input className="col-span-1 border-2 border-foreground rounded-xl px-3 py-2.5 bg-background" placeholder="Phone" value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="takeaway-phone" />
            </div>
            <input className="w-full border-2 border-foreground rounded-xl px-3 py-2.5 bg-background" placeholder="Email (optional)" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="takeaway-email" />
            {fulfilment === "delivery" && (
              <textarea className="w-full border-2 border-foreground rounded-xl px-3 py-2.5 bg-background" rows={2} placeholder="Delivery address" value={address} onChange={(e) => setAddress(e.target.value)} data-testid="takeaway-address" />
            )}
            <textarea className="w-full border-2 border-foreground rounded-xl px-3 py-2.5 bg-background" rows={2} placeholder="Notes for the kitchen (allergies, no onion, etc.)" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="takeaway-notes" />

            <div className="border-t-2 border-foreground/10 pt-4 space-y-1 font-mono-plex text-sm">
              <div className="flex justify-between"><span>Subtotal</span><span data-testid="takeaway-subtotal">{money(subtotal)}</span></div>
              {deliveryFee > 0 && <div className="flex justify-between"><span>Delivery</span><span>{money(deliveryFee)}</span></div>}
              <div className="flex justify-between font-head font-extrabold text-lg pt-1"><span>Total</span><span data-testid="takeaway-total">{money(total)}</span></div>
            </div>

            {!onlinePayment && (
              <p className="text-sm text-foreground/60 text-center">Pay when you {fulfilment === "delivery" ? "receive your order" : "collect"} — no card needed now.</p>
            )}

            <button
              onClick={submit}
              disabled={busy}
              data-testid="takeaway-pay-btn"
              className="w-full font-head font-extrabold text-lg bg-secondary text-secondary-foreground border-2 border-foreground rounded-full py-3.5 hover:translate-y-[2px] transition-transform disabled:opacity-60 flex items-center justify-center gap-2"
            >
              {busy ? <><Loader2 size={18} className="animate-spin" /> {onlinePayment ? "Redirecting…" : "Placing order…"}</>
                : onlinePayment ? `Pay ${money(total)} & Order` : "Place order"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
